// Remittances: cash a department sends to central treasury, and the department's cash position.
// Money is ALWAYS integer ngwee here. A remittance is DECLARED by the department; central
// treasury separately records what arrived and matches the two (see src/data/reconciliation.ts).
import { supabase } from '@/lib/supabase'
import { currentUserId } from './auth'
import { DataError, unwrap } from './errors'
import type { PaymentMethod } from './dues'

export type MovementStatus = 'declared' | 'partially_settled' | 'settled' | 'disputed' | 'cancelled'

export const MOVEMENT_STATUS_LABELS: Record<MovementStatus, string> = {
  declared: 'Waiting for central treasury',
  partially_settled: 'Partly confirmed',
  settled: 'Confirmed received',
  disputed: 'Under query',
  cancelled: 'Cancelled',
}

export interface Remittance {
  id: string
  departmentId: string
  declaredNgwee: number
  /** How much of it central treasury has matched to money it actually received. */
  matchedNgwee: number
  method: PaymentMethod
  movedOn: string
  bankRef: string | null
  slipNo: string | null
  handedTo: string | null
  handedBy: string | null
  note: string | null
  status: MovementStatus
  cancelReason: string | null
}

type Row = {
  id: string
  department_id: string
  declared_amount_ngwee: number
  method: PaymentMethod
  moved_on: string
  bank_ref: string | null
  slip_no: string | null
  handed_to: string | null
  handed_by: string | null
  note: string | null
  status: MovementStatus
  cancel_reason: string | null
}

const COLS = 'id, department_id, declared_amount_ngwee, method, moved_on, bank_ref, slip_no, handed_to, handed_by, note, status, cancel_reason'

/** The department's remittances, newest first, with how much of each central has confirmed. */
export async function listRemittances(departmentId: string): Promise<Remittance[]> {
  const [rows, recon] = await Promise.all([
    supabase
      .from('cash_movement')
      .select(COLS)
      .eq('department_id', departmentId)
      .eq('direction', 'dept_to_central')
      .order('moved_on', { ascending: false })
      .order('created_at', { ascending: false }),
    supabase.from('v_remittance_reconciliation').select('movement_id, matched_ngwee').eq('department_id', departmentId),
  ])
  const matched = new Map(
    (unwrap(recon as never, 'Could not load what central treasury has confirmed.') as { movement_id: string; matched_ngwee: number }[]).map(
      (r) => [r.movement_id, Number(r.matched_ngwee)],
    ),
  )
  return (unwrap(rows as never, 'Could not load the remittances.') as Row[]).map((r) => ({
    id: r.id,
    departmentId: r.department_id,
    declaredNgwee: Number(r.declared_amount_ngwee),
    matchedNgwee: matched.get(r.id) ?? 0,
    method: r.method,
    movedOn: r.moved_on,
    bankRef: r.bank_ref,
    slipNo: r.slip_no,
    handedTo: r.handed_to,
    handedBy: r.handed_by,
    note: r.note,
    status: r.status,
    cancelReason: r.cancel_reason,
  }))
}

export interface RemittanceInput {
  amountNgwee: number
  method: PaymentMethod
  movedOn: string
  bankRef?: string
  slipNo?: string
  /** Who physically received the cash (required for cash). */
  handedTo?: string
  handedBy?: string
  note?: string
}

const clean = (s?: string) => (s && s.trim() ? s.trim() : null)

/**
 * Declare a remittance and return its id. `clientRef` makes a retry (double tap, lost
 * connection) return the same row instead of declaring the money twice.
 */
export async function declareRemittance(departmentId: string, i: RemittanceInput, clientRef: string): Promise<string> {
  if (!Number.isSafeInteger(i.amountNgwee) || i.amountNgwee <= 0) {
    throw new DataError({ code: 'P0001', message: 'The amount must be greater than zero.' })
  }
  if (i.method === 'cash' && !clean(i.handedTo)) {
    throw new DataError({ code: 'P0001', message: 'Say who received the cash.' })
  }
  const userId = await currentUserId()
  const res = await supabase
    .from('cash_movement')
    .insert({
      department_id: departmentId,
      direction: 'dept_to_central',
      declared_amount_ngwee: i.amountNgwee,
      method: i.method,
      moved_on: i.movedOn,
      bank_ref: clean(i.bankRef),
      slip_no: clean(i.slipNo),
      handed_to: clean(i.handedTo),
      handed_by: clean(i.handedBy),
      note: clean(i.note),
      status: 'declared',
      client_ref: clientRef,
      created_by: userId,
    })
    .select('id')
    .single()
  if (res.error) {
    if (res.error.code === '23505') {
      const prior = await supabase
        .from('cash_movement')
        .select('id')
        .eq('department_id', departmentId)
        .eq('client_ref', clientRef)
        .maybeSingle()
      if (prior.data) {
        // The first try reached the server but its answer was lost; apply what is on the screen now
        // (a declaration can still be corrected until central treasury starts matching it).
        const id = (prior.data as { id: string }).id
        await supabase
          .from('cash_movement')
          .update({
            declared_amount_ngwee: i.amountNgwee,
            method: i.method,
            moved_on: i.movedOn,
            bank_ref: clean(i.bankRef),
            slip_no: clean(i.slipNo),
            handed_to: clean(i.handedTo),
            handed_by: clean(i.handedBy),
            note: clean(i.note),
          })
          .eq('id', id)
          .eq('status', 'declared')
        return id
      }
    }
    throw new DataError(res.error, 'Could not save the remittance.')
  }
  return (res.data as { id: string }).id
}

/** Cancel a declaration that central treasury has not started matching (a reason is required). */
export async function cancelRemittance(id: string, reason: string): Promise<void> {
  if (!reason.trim()) throw new DataError({ code: 'P0001', message: 'Give a reason for cancelling.' })
  const res = await supabase
    .from('cash_movement')
    .update({ status: 'cancelled', cancel_reason: reason.trim() })
    .eq('id', id)
    .eq('status', 'declared')
    .select('id')
  if (res.error) throw new DataError(res.error, 'Could not cancel the remittance.')
  if (((res.data as unknown[]) ?? []).length === 0) {
    throw new DataError(
      { code: 'P0001', message: 'Central treasury has already started confirming this one, so it cannot be cancelled. Ask them to undo the match first.' },
    )
  }
}

export interface Position {
  /** Dues and other money collected, remitted to central, spent from the department's cash. */
  collectedNgwee: number
  remittedNgwee: number
  receivedFromCentralNgwee: number
  spentLocalNgwee: number
  spentCentralNgwee: number
  /** What the department should be holding now (running total up to the quarter asked for). */
  cashOnHandNgwee: number
  quarterId: string
}

export interface DepartmentPosition extends Position {
  departmentId: string
}

/**
 * Cash positions for several quarters in one call: one department's history, or (leaving the
 * department out) every department the caller can read. Central departments are never included.
 * A quarter with no row (not yet opened) is simply missing from the result.
 */
export async function listPositions(opts: { departmentId?: string; quarterIds: string[] }): Promise<DepartmentPosition[]> {
  if (opts.quarterIds.length === 0) return []
  let q = supabase
    .from('v_department_position')
    .select('department_id, quarter_id, collected_ngwee, remitted_ngwee, received_from_central_ngwee, spent_local_ngwee, spent_central_ngwee, cash_on_hand_ngwee')
    .in('quarter_id', opts.quarterIds)
  if (opts.departmentId) q = q.eq('department_id', opts.departmentId)
  const res = await q.order('quarter_id').order('department_id')
  return (
    unwrap(res as never, 'Could not load the cash positions.') as {
      department_id: string
      quarter_id: string
      collected_ngwee: number
      remitted_ngwee: number
      received_from_central_ngwee: number
      spent_local_ngwee: number
      spent_central_ngwee: number
      cash_on_hand_ngwee: number
    }[]
  ).map((r) => ({
    departmentId: r.department_id,
    quarterId: r.quarter_id,
    collectedNgwee: Number(r.collected_ngwee),
    remittedNgwee: Number(r.remitted_ngwee),
    receivedFromCentralNgwee: Number(r.received_from_central_ngwee),
    spentLocalNgwee: Number(r.spent_local_ngwee),
    spentCentralNgwee: Number(r.spent_central_ngwee),
    cashOnHandNgwee: Number(r.cash_on_hand_ngwee),
  }))
}

/** The department's cash position for a quarter, with the running balance carried forward. */
export async function getPosition(departmentId: string, quarterId: string): Promise<Position | null> {
  const res = await supabase
    .from('v_department_position')
    .select('quarter_id, collected_ngwee, remitted_ngwee, received_from_central_ngwee, spent_local_ngwee, spent_central_ngwee, cash_on_hand_ngwee')
    .eq('department_id', departmentId)
    .eq('quarter_id', quarterId)
    .maybeSingle()
  const r = unwrap(res as never, 'Could not load the cash position.') as {
    quarter_id: string
    collected_ngwee: number
    remitted_ngwee: number
    received_from_central_ngwee: number
    spent_local_ngwee: number
    spent_central_ngwee: number
    cash_on_hand_ngwee: number
  } | null
  if (!r) return null
  return {
    quarterId: r.quarter_id,
    collectedNgwee: Number(r.collected_ngwee),
    remittedNgwee: Number(r.remitted_ngwee),
    receivedFromCentralNgwee: Number(r.received_from_central_ngwee),
    spentLocalNgwee: Number(r.spent_local_ngwee),
    spentCentralNgwee: Number(r.spent_central_ngwee),
    cashOnHandNgwee: Number(r.cash_on_hand_ngwee),
  }
}
